import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

/* ──────────────────────────────────────────────────────────────────────
   Token storage — localStorage with two keys.
   In a higher-security deployment swap for httpOnly cookies set by the API.
   ────────────────────────────────────────────────────────────────────── */
const AT_KEY = "np_at"; // access token
const RT_KEY = "np_rt"; // refresh token

export const tokenStore = {
  get access(): string | null {
    return window.localStorage.getItem(AT_KEY);
  },
  get refresh(): string | null {
    return window.localStorage.getItem(RT_KEY);
  },
  set(at: string, rt: string) {
    window.localStorage.setItem(AT_KEY, at);
    window.localStorage.setItem(RT_KEY, rt);
  },
  clear() {
    window.localStorage.removeItem(AT_KEY);
    window.localStorage.removeItem(RT_KEY);
  },
};

/* ──────────────────────────────────────────────────────────────────────
   Axios client — single instance, refresh-on-401 interceptor that
   coalesces concurrent refresh attempts so we only make ONE refresh call
   even if 12 parallel requests fail with 401.
   ────────────────────────────────────────────────────────────────────── */

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const rt = tokenStore.refresh;
  if (!rt) throw new Error("no_refresh_token");

  // Use a bare axios call (no interceptor) so a 401 here doesn't loop.
  const res = await axios.post<{
    access_token: string;
    refresh_token: string;
  }>("/api/auth/refresh", { refresh_token: rt }, { baseURL: "" });

  tokenStore.set(res.data.access_token, res.data.refresh_token);
  return res.data.access_token;
}

export const api: AxiosInstance = axios.create({
  baseURL: "",
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
});

api.interceptors.request.use((cfg) => {
  const at = tokenStore.access;
  if (at && cfg.headers) cfg.headers.Authorization = `Bearer ${at}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const cfg = err.config as RetriableConfig | undefined;
    const status = err.response?.status;

    // No retry on /auth/login or /auth/refresh — those are the bottom of the stack.
    const path = (cfg?.url ?? "").toString();
    const isAuthEndpoint =
      path.includes("/api/auth/login") || path.includes("/api/auth/refresh");

    if (status === 401 && cfg && !cfg._retry && !isAuthEndpoint) {
      cfg._retry = true;

      try {
        // Coalesce parallel refreshes.
        refreshPromise = refreshPromise ?? performRefresh();
        const newAt = await refreshPromise;
        refreshPromise = null;

        if (cfg.headers) cfg.headers.Authorization = `Bearer ${newAt}`;
        return api.request(cfg);
      } catch (refreshErr) {
        refreshPromise = null;
        tokenStore.clear();
        // Trigger a redirect by emitting a custom event the AuthProvider listens to.
        window.dispatchEvent(new CustomEvent("np:auth:expired"));
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(err);
  }
);

/** Extract a human-readable error message from an Axios error. */
export function explainError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response) {
      const data = err.response.data as { detail?: unknown } | undefined;
      const d = data?.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d) && d.length > 0) {
        // pydantic validation error array
        const first = d[0] as { msg?: string } | undefined;
        if (first?.msg) return first.msg;
      }
      return `HTTP ${err.response.status}`;
    }
    if (err.request) return "Server unreachable";
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

export type AxiosFetchConfig = AxiosRequestConfig;
